import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import {
  enrolMyPublic,
  getMyPublicClasses,
  TAddPerson,
  TMyPublicClass,
  TPublicEnrolment,
} from '../../../requests/customerPortal';
import {
  btnGhost,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  Toolbar,
} from '../../Admin/ui';
import {
  AddPersonForm,
  fmtDateTime,
  fmtNum,
  LoadFailed,
  retryUnless4xx,
} from '../shared';

function outcome(r: TPublicEnrolment): string {
  const bits: string[] = [];
  bits.push(
    r.status === 'Waiting List'
      ? 'added to the waiting list'
      : `seat booked · ${fmtNum(r.booked)} of ${r.seats ?? '—'} taken`,
  );
  bits.push(
    r.billing === 'program'
      ? 'on your program'
      : 'billed as a one-off (finance will follow up)',
  );
  if (r.confirmation) {
    bits.push(
      r.confirmation.status === 'sent'
        ? `confirmation sent to ${r.confirmation.to}`
        : `confirmation queued for ${r.confirmation.to}`,
    );
  }
  return bits.join(' · ');
}

/**
 * Public classes — the schedule anyone can book onto, and the "New Public
 * Enrollment" the Salesforce admin portal used to carry (T7.10): pick a
 * class, pick or add one of your people, confirm. The seat goes through
 * the same rules as the office's (one seat per person, the room's cap,
 * your Team Pass if you have one) and the learner gets the confirmation.
 */
const AdminPublicClasses: React.FC = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [enrolling, setEnrolling] = useState<TMyPublicClass | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'public-classes', q],
    queryFn: () => getMyPublicClasses(q || undefined),
  });

  const enrol = useMutation({
    mutationFn: (vars: { classId: string; body: TAddPerson }) =>
      enrolMyPublic(vars.classId, vars.body),
    onSuccess: (r) => {
      showToast(`Enrolled — ${outcome(r)}`, 'success');
      setEnrolling(null);
      qc.invalidateQueries({ queryKey: ['my', 'public-classes'] });
      qc.invalidateQueries({ queryKey: ['my', 'registrations'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <>
      <PageHeader
        title="Public classes"
        meta="Open classes on the public schedule. Enrol one of your people on any of them; the seat counts against your program when you have one."
      />
      <PageBody>
        {enrolling && (
          <Card
            title={`Enrol someone on ${enrolling.name ?? 'this class'}`}
            className="mb-6"
            right={
              <span className="text-xs text-gray-bold">
                {fmtDateTime(enrolling.start)}
                {enrolling.seats != null
                  ? ` · ${enrolling.booked} of ${enrolling.seats} seats taken`
                  : ''}
              </span>
            }
          >
            <div className="px-5 pt-4">
              <AddPersonForm
                label="Who is attending"
                busy={enrol.isPending}
                allowWaitlist={
                  enrolling.seats != null && enrolling.booked >= enrolling.seats
                }
                onSubmit={(body) =>
                  enrol.mutate({ classId: enrolling.id, body })
                }
                onCancel={() => setEnrolling(null)}
              />
            </div>
            <div className="px-5 pb-4 text-xs text-gray-bold">
              They get the registration email straight away. One seat per person
              per class; a full class offers the waiting list instead.
            </div>
          </Card>
        )}
        <Toolbar>
          <input
            className={inputClass}
            placeholder="Search by class name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {data && (
            <span className="text-sm text-gray-bold">
              {data.total} upcoming
            </span>
          )}
        </Toolbar>
        <Card>
          {isLoading ? (
            <LoadingSpinner />
          ) : error || !data ? (
            <LoadFailed error={error} />
          ) : data.items.length === 0 ? (
            <EmptyState
              title="Nothing on the public schedule right now"
              hint="Check back soon, or request a private course for your team."
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Class</th>
                  <th className={thClass}>Starts</th>
                  <th className={thClass}>Format</th>
                  <th className={`${thClass} text-right`}>Seats</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => {
                  const full = c.seats != null && c.booked >= c.seats;
                  return (
                    <tr key={c.id}>
                      <td className={tdClass}>
                        <div className="font-medium text-gray-five">
                          {c.name ?? '(unnamed class)'}
                        </div>
                        <div className="text-xs text-gray-bold">
                          {c.category ?? '—'}
                          {c.durationHours != null
                            ? ` · ${fmtNum(c.durationHours, 1)} h`
                            : ''}
                        </div>
                      </td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {fmtDateTime(c.start)}
                        {c.timeZone ? (
                          <span className="ml-1 text-xs text-gray-bold">
                            {c.timeZone}
                          </span>
                        ) : null}
                      </td>
                      <td className={tdClass}>
                        <Pill value={c.location} tone="info" />
                      </td>
                      <td className={`${tdClass} text-right whitespace-nowrap`}>
                        {c.seats != null
                          ? `${c.booked} / ${c.seats}`
                          : c.booked}
                        {full && (
                          <span className="ml-2 text-xs text-yellow-900">
                            full
                          </span>
                        )}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        <button
                          type="button"
                          className={
                            enrolling?.id === c.id ? btnGhost : btnSecondary
                          }
                          disabled={enrol.isPending}
                          onClick={() =>
                            setEnrolling(enrolling?.id === c.id ? null : c)
                          }
                        >
                          {enrolling?.id === c.id
                            ? 'Close'
                            : full
                            ? 'Join waiting list'
                            : 'Enrol someone'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
};

export default AdminPublicClasses;
